import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import { Grid, Header, Button, Message } from 'semantic-ui-react';
import { useBookWorksheet } from '../../hooks/useBookWorksheet';
import { bookValidationSchema } from '../../utils/validationSchemas';
import { FormikInput, FormikTextArea, FormikSelect, FormikField, FormChangeDetector } from '../common';

/**
 * BookForm component
 * Form for creating and editing books
 * @param {Object} props - Component props
 * @param {Function} props.onSave - Callback when the form is saved
 * @returns {JSX.Element} BookForm component
 */
const BookForm = ({ onSave }) => {
  const {
    selectedBook,
    bookId,
    litGroupId,
    createBook,
    updateBook,
    booksLoading
  } = useBookWorksheet();

  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState(null);
  const [formChanged, setFormChanged] = useState(false);
  const formikRef = useRef();

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Form submission handler
  const handleSubmit = async (values) => {
    setFormLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      let result;

      if (bookId) {
        // Update existing book
        result = await updateBook({
          bookId,
          data: values
        });
        setSuccessMessage('Book updated successfully!');
      } else if (litGroupId) {
        // Create new book
        result = await createBook({
          litGroupId,
          data: values
        });
        setSuccessMessage('Book created successfully!');
      } else {
        throw new Error('No literary group selected');
      }

      setFormChanged(false);

      // Call the onSave callback if provided
      if (onSave) {
        onSave(result);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while saving the book');
    } finally {
      setFormLoading(false);
    }
  };

  // Reset form to initial values
  const handleReset = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
      setFormChanged(false);
    }
  };

  // Get initial values for the form
  const getInitialValues = () => {
    return {
      title: selectedBook?.name || '',
      author: selectedBook?.author || '',
      description: selectedBook?.description || '',
      genre: selectedBook?.genre || '',
      status: selectedBook?.status || 'active',
      publicationYear: selectedBook?.publicationYear || ''
    };
  };

  // Show message if no literary group is selected
  if (!litGroupId) {
    return (
      <Message info>
        <Message.Header>No Literary Group Selected</Message.Header>
        <p>Please select a literary group first to create or edit a book.</p>
      </Message>
    );
  }

  return (
    <div className="bw-form-container">
      <Header as="h3" dividing>
        {bookId ? 'Edit Book' : 'Create New Book'}
      </Header>

      <Formik
        innerRef={formikRef}
        initialValues={getInitialValues()}
        validationSchema={bookValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, isValid, dirty }) => (
          <Form>
            <FormChangeDetector
              onChange={setFormChanged}
              initialValues={getInitialValues()}
            />

            <Grid divided={false}>
              <Grid.Row className="">
                <Grid.Column width={16}>
                  <FormikField
                    name="name"
                    component={FormikInput}
                    label="Book Title"
                    placeholder="Enter book title"
                    fluid
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={16}>
                  <FormikField
                    className="formikCleanup formikTextArea"

                    name="description"
                    component={FormikTextArea}
                    label="Description"
                    placeholder="Enter a description for this book"
                    rows={4}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row className="noTopPad">
                <Grid.Column width={8}>
                  <FormikField
                    name="author"
                    component={FormikInput}
                    label="Author"
                    placeholder="Enter author name"
                    fluid
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <FormikField
                    name="series"
                    component={FormikInput}
                    label="Series (Optional)"
                    placeholder="Enter series name"
                    fluid
                  />
                </Grid.Column>
                <Grid.Column width={4}>
                  <FormikField
                    name="publicationYear"
                    component={FormikInput}
                    label="Publication Year"
                    placeholder="YYYY"
                    fluid
                  />
                </Grid.Column>
                <Grid.Column width={4}>
                  <FormikField
                    name="status"
                    component={FormikSelect}
                    label="Status"
                    options={[
                      { key: 'active', text: 'Active', value: 'active' },
                      { key: 'draft', text: 'Draft', value: 'draft' },
                      { key: 'archived', text: 'Archived', value: 'archived' }
                    ]}
                    placeholder="Select status"
                    fluid
                  />
                </Grid.Column>
              </Grid.Row>



              {/* Status messages */}
              {(error || successMessage) && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    {error && (
                      <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                      </Message>
                    )}

                    {successMessage && (
                      <Message positive>
                        <Message.Header>Success</Message.Header>
                        <p>{successMessage}</p>
                      </Message>
                    )}
                  </Grid.Column>
                </Grid.Row>
              )}

              {/* Form buttons */}
              <Grid.Row>
                <Grid.Column width={16}>
                  <Button
                    primary
                    type="submit"
                    loading={formLoading || booksLoading === 'loading'}
                    disabled={!formChanged || !isValid}
                  >
                    {bookId ? 'Update Book' : 'Create Book'}
                  </Button>

                  {formChanged && (
                    <Button
                      type="button"
                      onClick={handleReset}
                    >
                      Cancel Changes
                    </Button>
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default BookForm;
